/**
 * Importing candidates from a spreadsheet (recruitment: Naukri / LinkedIn / Indeed exports, a
 * recruiter's own sheet). Pure, so the browser (matching columns, a live preview) and the server
 * (the only place a row is TRUSTED) clean and classify rows with the same code, and every rule is a
 * test rather than a screen.
 *
 * The server never believes the browser: it re-cleans every row it is sent with `cleanImportRow` and
 * decides duplicates itself with `classifyImportRows`.
 */

import type { ExperienceLevel } from '../enums.js';
import { safeEmailHref } from '../notifications/email-doc.js';

// This package is built without DOM or Node typings (it also compiles inside @hrms/payroll, whose tsconfig has
// "types": []), so the runtime globals used here are declared locally, only as far as this file uses them.
// Node, browsers and the web view all provide them at runtime; nothing here changes behaviour.
declare const URL: { new (input: string): { protocol: string; toString(): string } };

export const IMPORT_FIELDS = [
  'fullName',
  'firstName',
  'lastName',
  'email',
  'phone',
  'experience',
  'skills',
  'linkedinUrl',
  'coverNote',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  fullName: 'Full name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Mobile number',
  experience: 'Experience (years)',
  skills: 'Skills',
  linkedinUrl: 'LinkedIn / profile link',
  coverNote: 'Note',
};

/** Most rows a single import takes: the same cap as the project-task import, and it keeps one request short. */
export const MAX_IMPORT_ROWS = 500;

/** `Total Experience (Yrs)` and `total_experience` are the same header. */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, '');
}

/** What exports of Naukri, LinkedIn, Indeed and a hand-made sheet call each column (normalised). */
const SYNONYMS: Record<ImportField, readonly string[]> = {
  fullName: ['name', 'fullname', 'candidatename', 'applicantname', 'candidate'],
  firstName: ['firstname', 'givenname', 'fname'],
  lastName: ['lastname', 'surname', 'familyname', 'lname'],
  email: ['email', 'emailid', 'emailaddress', 'mail', 'candidateemail', 'primaryemail', 'emailidprimary'],
  phone: [
    'phone',
    'phonenumber',
    'phonenumbers',
    'mobile',
    'mobilenumber',
    'mobileno',
    'contactnumber',
    'contact',
    'cell',
    'telephone',
  ],
  experience: ['experience', 'totalexperience', 'totalexp', 'workexperience', 'yearsofexperience', 'exp', 'experienceyears'],
  skills: ['skills', 'keyskills', 'skillset', 'technicalskills', 'topskills'],
  linkedinUrl: ['linkedin', 'linkedinurl', 'linkedinprofile', 'profileurl', 'profilelink', 'url', 'profile'],
  coverNote: ['note', 'notes', 'remarks', 'comments', 'summary', 'headline', 'currentemployer', 'currentcompany'],
};

/**
 * For each field, the index of the column that probably holds it, or null. A column is used once;
 * the more specific fields (first/last name) take their column before "full name" can claim it.
 */
export function suggestMapping(headers: readonly string[]): Record<ImportField, number | null> {
  const normalised = headers.map(normalizeHeader);
  const taken = new Set<number>();
  const result = {} as Record<ImportField, number | null>;
  const order: ImportField[] = [
    'email',
    'firstName',
    'lastName',
    'fullName',
    'phone',
    'experience',
    'skills',
    'linkedinUrl',
    'coverNote',
  ];
  for (const field of order) {
    let found: number | null = null;
    for (const synonym of SYNONYMS[field]) {
      const index = normalised.findIndex((h, i) => h === synonym && !taken.has(i));
      if (index >= 0) {
        found = index;
        break;
      }
    }
    if (found !== null) taken.add(found);
    result[field] = found;
  }
  return result;
}

/** A raw row as read from the sheet, after the columns were matched: all text, nothing trusted. */
export type ImportRowInput = Partial<Record<ImportField, string>>;

export interface CleanImportRow {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  experienceLevel: ExperienceLevel | null;
  /** The years as written in the sheet, when it gave a number. */
  experienceYears: number | null;
  skillTags: string[];
  linkedinUrl: string | null;
  coverNote: string | null;
}

const MAX_SKILLS = 20;

/** A leading `= + - @` makes a spreadsheet read a cell as a formula when someone exports it again. */
export function stripFormulaPrefix(value: string): string {
  return value.replace(/^[\s=+\-@\t\r]+/, '').trim();
}

export function splitFullName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0]!, lastName: '' };
  return { firstName: parts[0]!, lastName: parts.slice(1).join(' ') };
}

/**
 * E.164 (`+919876543210`) from whatever a sheet holds. `defaultCountryCode` is digits without a plus
 * (`91`) and is used only for a bare national number (10 digits, or 0 + 10). Anything that cannot be
 * made into a plausible number is null; a bad phone does not sink the row.
 */
export function normalizePhone(raw: string | undefined, defaultCountryCode: string | null): string | null {
  if (!raw) return null;
  let text = raw.trim();
  // A spreadsheet turns 9876543210 into 9.87654321E9.
  if (/^\d(\.\d+)?e\+?\d+$/i.test(text)) return null;
  const hasPlus = text.startsWith('+');
  text = text.replace(/[\s\-().]/g, '');
  let digits = text.replace(/^\+/, '').replace(/\D/g, '');
  if (digits.length === 0) return null;
  if (hasPlus) {
    // as given
  } else if (digits.startsWith('00')) {
    digits = digits.slice(2);
  } else if (defaultCountryCode && digits.length === 10) {
    digits = defaultCountryCode + digits;
  } else if (defaultCountryCode && digits.length === 11 && digits.startsWith('0')) {
    digits = defaultCountryCode + digits.slice(1);
  } else if (!defaultCountryCode || !digits.startsWith(defaultCountryCode)) {
    // No plus, no 00 and no country to assume: guessing one would store somebody else's number.
    return null;
  }
  const e164 = `+${digits}`;
  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
}

/** First number in the text, as years: "5", "5.5", "5 Years 3 Months", "5-8 yrs" -> 5. */
export function experienceYears(raw: string | undefined): number | null {
  if (!raw) return null;
  const match = raw.match(/\d+(?:\.\d+)?/);
  if (!match) return null;
  const years = Number(match[0]);
  return Number.isFinite(years) && years >= 0 && years < 70 ? years : null;
}

/** The apply form's own bands: entry 0-2, mid 2-5, senior 5-8, lead 8+. */
export function experienceLevelFromYears(years: number | null): ExperienceLevel | null {
  if (years === null) return null;
  if (years < 2) return 'entry';
  if (years < 5) return 'mid';
  if (years < 8) return 'senior';
  return 'lead';
}

export function splitSkills(raw: string | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const piece of raw.split(/[,;|\n/]+/)) {
    const tag = stripFormulaPrefix(piece).slice(0, 100);
    const key = tag.toLowerCase();
    if (tag && !seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
    if (tags.length === MAX_SKILLS) break;
  }
  return tags;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function cleanUrl(raw: string | undefined): string | null {
  const text = raw?.trim();
  if (!text) return null;
  const candidate = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  // No `URL` global in this package (see `safeEmailHref`): the host must be a plain hostname with an
  // optional port, which is what turns `javascript:alert(1)` (host `javascript`, port `alert(1)`) away.
  const match = /^(https?):\/\/([^/?#]*)(.*)$/i.exec(candidate);
  if (!match) return null;
  const [, scheme = '', host = '', rest = ''] = match;
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*(:\d{1,5})?$/i.test(host)) return null;
  return safeEmailHref(`${scheme.toLowerCase()}://${host.toLowerCase()}${rest}`);
}

export type CleanResult =
  | { ok: true; row: CleanImportRow; warnings: string[] }
  | { ok: false; reason: string };

export function cleanImportRow(
  raw: ImportRowInput,
  options: { defaultCountryCode: string | null },
): CleanResult {
  const warnings: string[] = [];

  const email = (raw.email ?? '').trim().toLowerCase();
  if (!email) return { ok: false, reason: 'No email address' };
  if (email.length > 254 || !EMAIL.test(email)) return { ok: false, reason: `"${email.slice(0, 60)}" is not a valid email` };

  let firstName = stripFormulaPrefix(raw.firstName ?? '');
  let lastName = stripFormulaPrefix(raw.lastName ?? '');
  if (!firstName && raw.fullName) {
    const split = splitFullName(stripFormulaPrefix(raw.fullName));
    firstName = split.firstName;
    lastName = lastName || split.lastName;
  }
  if (!firstName) return { ok: false, reason: 'No name' };
  // The product needs a last name; a single-name candidate gets a dash rather than being refused.
  if (!lastName) {
    lastName = '-';
    warnings.push('No last name, saved as "-"');
  }

  const phoneRaw = raw.phone?.trim();
  const phone = normalizePhone(phoneRaw, options.defaultCountryCode);
  if (phoneRaw && !phone) warnings.push('Phone number not understood, left out');

  const linkedinRaw = raw.linkedinUrl?.trim();
  const linkedinUrl = cleanUrl(raw.linkedinUrl);
  if (linkedinRaw && !linkedinUrl) warnings.push('Profile link not understood, left out');

  const note = raw.coverNote ? stripFormulaPrefix(raw.coverNote).slice(0, 2000) : '';

  return {
    ok: true,
    row: {
      firstName: firstName.slice(0, 100),
      lastName: lastName.slice(0, 100),
      email,
      phone,
      experienceLevel: experienceLevelFromYears(experienceYears(raw.experience)),
      experienceYears: experienceYears(raw.experience),
      skillTags: splitSkills(raw.skills),
      linkedinUrl,
      coverNote: note || null,
    },
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Duplicates
// ---------------------------------------------------------------------------

export type ImportRowStatus =
  | 'new'
  /** Already an application to THIS job (same email, or same phone). Skipped, or only blanks filled. */
  | 'duplicate_here'
  /** An earlier row of this same file has the same email or phone. */
  | 'duplicate_in_file'
  /** Applied to a DIFFERENT job before: imported, and linked to that profile. */
  | 'linked_elsewhere'
  | 'invalid';

export interface ExistingApplication {
  id: string;
  email: string;
  /** E.164 */
  phone: string | null;
}

export interface ClassifiedRow {
  /** Position in the file, from 0. */
  index: number;
  status: ImportRowStatus;
  reason?: string;
  /** For `duplicate_here`: the application it matched. */
  matchedCandidateId?: string;
}

export function classifyImportRows(
  entries: ReadonlyArray<{ clean: CleanImportRow | null; invalidReason?: string }>,
  here: readonly ExistingApplication[],
  elsewhere: { emails: ReadonlySet<string>; phones: ReadonlySet<string> },
): ClassifiedRow[] {
  const hereByEmail = new Map(here.map((a) => [a.email.toLowerCase(), a]));
  const hereByPhone = new Map(here.filter((a) => a.phone).map((a) => [a.phone!, a]));
  const seenEmails = new Set<string>();
  const seenPhones = new Set<string>();

  return entries.map(({ clean, invalidReason }, index): ClassifiedRow => {
    if (!clean) return { index, status: 'invalid', reason: invalidReason ?? 'Could not read this row' };

    const byEmail = hereByEmail.get(clean.email);
    if (byEmail) {
      return { index, status: 'duplicate_here', reason: 'Already applied to this job with this email', matchedCandidateId: byEmail.id };
    }
    const byPhone = clean.phone ? hereByPhone.get(clean.phone) : undefined;
    if (byPhone) {
      return { index, status: 'duplicate_here', reason: 'Already applied to this job with this phone number', matchedCandidateId: byPhone.id };
    }
    if (seenEmails.has(clean.email)) {
      return { index, status: 'duplicate_in_file', reason: 'This email appears earlier in the file' };
    }
    if (clean.phone && seenPhones.has(clean.phone)) {
      return { index, status: 'duplicate_in_file', reason: 'This phone number appears earlier in the file' };
    }
    seenEmails.add(clean.email);
    if (clean.phone) seenPhones.add(clean.phone);

    if (elsewhere.emails.has(clean.email) || (clean.phone && elsewhere.phones.has(clean.phone))) {
      return { index, status: 'linked_elsewhere', reason: 'Applied to another job before; will be linked to that profile' };
    }
    return { index, status: 'new' };
  });
}

export interface ImportSummary {
  new: number;
  duplicateHere: number;
  duplicateInFile: number;
  linkedElsewhere: number;
  invalid: number;
}

export function summariseClassified(rows: readonly ClassifiedRow[]): ImportSummary {
  const count = (status: ImportRowStatus) => rows.filter((r) => r.status === status).length;
  return {
    new: count('new'),
    duplicateHere: count('duplicate_here'),
    duplicateInFile: count('duplicate_in_file'),
    linkedElsewhere: count('linked_elsewhere'),
    invalid: count('invalid'),
  };
}

/** Cells in a downloaded report start with a quote when a spreadsheet would read them as a formula. */
export function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

// ---------------------------------------------------------------------------
// Reading a CSV
// ---------------------------------------------------------------------------

/** Excel in some locales saves a "CSV" with semicolons (or tabs). The header line tells which. */
export function detectDelimiter(text: string): ',' | ';' | '\t' {
  const firstLine = text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '';
  const count = (ch: string) => firstLine.split(ch).length - 1;
  const [comma, semicolon, tab] = [count(','), count(';'), count('\t')];
  if (tab > comma && tab > semicolon) return '\t';
  return semicolon > comma ? ';' : ',';
}

/** A CSV with quoted fields (`""` for a quote, delimiters and newlines inside quotes), CRLF or LF, and a BOM. */
export function parseDelimited(text: string, delimiter: string = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const source = text.replace(/^\uFEFF/, '');
  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === delimiter) endField();
    else if (ch === '\r') continue;
    else if (ch === '\n') endRow();
    else field += ch;
  }
  if (field.length > 0 || row.length > 0) endRow();
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}

/** The sheet to fill in, for people who start from nothing. */
export const IMPORT_TEMPLATE_CSV =
  'First Name,Last Name,Email,Mobile,Experience (years),Skills,LinkedIn\r\n' +
  'Asha,Rao,asha.rao@example.com,+919876543210,4,"React, Node.js",https://linkedin.com/in/asha-rao\r\n';
